import { sessionApi, ApiError } from "@/api/session";
import { UI } from "@/components/ui";

function escapeHtml(s: string) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Full-screen login page matching EasyShare shell tokens.
 * Auth: POST /api/session/create with MD5(password), same as client_nodejs.
 */
export function renderLoginPage(root: HTMLElement, onSuccess: () => void | Promise<void>) {
  root.innerHTML = `
    <div class="min-h-screen bg-ink-50 flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div class="absolute inset-0 pointer-events-none" style="background:
        radial-gradient(ellipse 80% 50% at 20% 0%, rgba(30,58,138,0.08), transparent),
        radial-gradient(ellipse 60% 40% at 100% 100%, rgba(30,58,138,0.06), transparent);"></div>
      <div class="relative w-full max-w-[420px]">
        <div class="flex items-center gap-3 mb-8 justify-center">
          <div class="w-11 h-11 rounded-lg bg-brand-900 flex items-center justify-center text-white font-bold text-lg shadow-soft">E</div>
          <div>
            <div class="text-lg font-semibold text-ink-900 leading-tight">EasyShare</div>
            <div class="text-xs text-ink-500">海外游戏分享 SDK 运营控制台</div>
          </div>
        </div>

        <div class="bg-white border border-ink-200 rounded-2xl shadow-soft overflow-hidden">
          <div class="px-6 pt-6 pb-4 border-b border-ink-100">
            <h1 class="text-base font-semibold text-ink-900">登录</h1>
            <p class="text-xs text-ink-500 mt-1">使用账号密码进入控制台。密码在本地 MD5 后提交（与 session client 一致）。</p>
          </div>
          <form id="login-form" class="px-6 py-5 space-y-4" autocomplete="on">
            <div>
              <label class="block text-xs font-medium text-ink-600 mb-1.5" for="login-user-id">用户 ID</label>
              <input id="login-user-id" name="username" type="text" required autocomplete="username"
                placeholder="例如 admin"
                class="w-full h-10 px-3 rounded-lg border border-ink-200 bg-white text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            </div>
            <div>
              <label class="block text-xs font-medium text-ink-600 mb-1.5" for="login-password">密码</label>
              <input id="login-password" name="password" type="password" required autocomplete="current-password"
                placeholder="请输入密码"
                class="w-full h-10 px-3 rounded-lg border border-ink-200 bg-white text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            </div>
            <label class="flex items-center gap-2 text-xs text-ink-600 select-none">
              <input id="login-remember" type="checkbox" checked class="rounded border-ink-300 text-brand-700 focus:ring-brand-500" />
              在本机记住登录状态
            </label>
            <div id="login-error" class="hidden text-xs text-danger-600 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2"></div>
            <button id="login-submit" type="submit"
              class="w-full h-10 rounded-lg bg-brand-900 text-white text-sm font-medium hover:bg-brand-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              登录
            </button>
          </form>
          <div class="px-6 py-4 bg-ink-50 border-t border-ink-100 text-[11px] text-ink-500 leading-relaxed">
            <div class="font-medium text-ink-700 mb-1">演示账号（MSW / 种子数据）</div>
            <div><code class="font-mono text-ink-800">admin</code> / <code class="font-mono">admin123</code> · SDK 管理员</div>
            <div><code class="font-mono text-ink-800">operator1</code> / <code class="font-mono">admin123</code> · 游戏运营</div>
            <div><code class="font-mono text-ink-800">developer1</code> / <code class="font-mono">admin123</code> · 游戏研发</div>
          </div>
        </div>
        <p class="text-center text-[11px] text-ink-400 mt-5">对接 session 服务 · POST /api/session/create</p>
      </div>
    </div>
  `;

  const form = root.querySelector("#login-form") as HTMLFormElement;
  const errEl = root.querySelector("#login-error") as HTMLElement;
  const btn = root.querySelector("#login-submit") as HTMLButtonElement;

  form.onsubmit = async (e) => {
    e.preventDefault();
    const userId = (root.querySelector("#login-user-id") as HTMLInputElement).value.trim();
    const password = (root.querySelector("#login-password") as HTMLInputElement).value;
    const remember = (root.querySelector("#login-remember") as HTMLInputElement).checked;
    if (!userId || !password) {
      errEl.textContent = "请输入用户 ID 和密码";
      errEl.classList.remove("hidden");
      return;
    }

    btn.disabled = true;
    btn.textContent = "登录中…";
    errEl.classList.add("hidden");

    try {
      await sessionApi.login(userId, password, remember);
      UI.toast("登录成功", "success");
      await onSuccess();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "登录失败";
      errEl.textContent = escapeHtml(message);
      errEl.classList.remove("hidden");
      btn.disabled = false;
      btn.textContent = "登录";
    }
  };
}
